import React from "react"
import styled, { createGlobalStyle } from "styled-components"
import colors from "@constants/colors"
import { cubicBezierBackgroundColor } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { Close } from "@components/icons"
import Modal from "react-modal"

export const Container = styled.div`
  flex-direction: column;
  display: flex;
  will-change: transform;
  min-height: 100vh;
  background-color: ${colors.white};
  pointer-events: initial;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    justify-content: space-between;
    transform: translateY(56px);
    transition: transform 250ms ${cubicBezierBackgroundColor};
    min-height: calc(100vh - (1 / 12 * 100vw));
    margin: calc(1 / 12 * 100vw) calc(0.5 / 12 * 100vw) 0;
    padding: calc(1 / 12 * 100vw) 0;
  }
`

export const Content = styled.div`
  @media ${breakpoints.laptop} {
    width: calc(10 / 12 * 100vw);
    padding: 0 calc(1 / 12 * 100vw);
  }
`

export const CloseColumn = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    flex-direction: column;
    flex-shrink: 0;
    align-items: flex-start;
    display: flex;
    width: calc(1 / 12 * 100vw);
  }
`

const CloseButton = styled.button`
  width: 56px;
  height: 56px;
  padding: 8px;
  border: none;
  border-radius: 2px;
  background: ${colors.dividerLineGray};
  cursor: pointer;

  &:focus {
    outline: solid ${colors.mediumDarkGray} thin;
  }
`

const ModalAdapter = ({
  className,
  modalClassName,
  bodyOpenClassName,
  ...props
}: any) => {
  return (
    // @ts-ignore
    <Modal className={modalClassName} portalClassName={className} {...props} />
  )
}

const StyledModal = styled(ModalAdapter).attrs({
  modalClassName: {
    base: "ReactModal__Content",
    afterOpen: "ReactModal__Content--after-open",
    beforeClose: "ReactModal__Content--before-close",
  },
  overlayClassName: {
    base: "ReactModal__Overlay",
    afterOpen: "ReactModal__Overlay--after-open",
    beforeClose: "ReactModal__Overlay--before-close",
  },
})`
  .ReactModal__Content {
    z-index: 1001;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    outline: none;
    pointer-events: none;

    @media ${breakpoints.laptop} {
      &--after-open {
        ${Container} {
          transform: translateY(0);
        }
      }

      &--before-close {
        ${Container} {
          transform: translateY(56px);
        }
      }
    }
  }

  .ReactModal__Overlay {
    z-index: 1001;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 1;
    transform: translateX(100%);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    will-change: opacity, transform;
    transition: opacity 150ms ${cubicBezierBackgroundColor},
      transform 250ms ${cubicBezierBackgroundColor};
    background: none;

    @media ${breakpoints.laptop} {
      opacity: 0;
      transform: translateX(0);
      background: rgba(0, 0, 0, 0.5);
    }

    &--after-open {
      opacity: 1;
      transform: translateX(0);
    }

    &--before-close {
      opacity: 1;
      transform: translateX(100%);

      @media ${breakpoints.laptop} {
        opacity: 0;
        transform: translateX(0);
      }
    }
  }
`

const GlobalStyle = createGlobalStyle`
  .ReactModal__Body--open {
    overflow: hidden;
  }
  .ReactModal__Html--open {
    overflow: hidden;
  }
` as any

export interface ModalBaseProps extends Modal.Props {}

interface ModalProps extends ModalBaseProps {
  children?: React.ReactNode
}

export default ({ children, ...rest }: ModalProps) => {
  React.useEffect(() => {
    if (rest.ariaHideApp !== false) {
      if (document.getElementById("root")) {
        Modal.setAppElement("#root")
      } else {
        Modal.setAppElement("#___gatsby")
      }
    }
  })

  return (
    <StyledModal {...rest} closeTimeoutMS={250}>
      <Container>
        <Content>{children}</Content>
        <CloseColumn>
          <CloseButton onClick={rest.onRequestClose} type="button">
            <Close strokeColor={colors.mediumDarkGray} />
          </CloseButton>
        </CloseColumn>
      </Container>
      <GlobalStyle />
    </StyledModal>
  )
}
